import React, { useState, useEffect } from "react"
import { Modal, Form, Select } from "antd"
import { defaultConfig } from "../../../utils/businessModalConfig"
import "./merchantModal.css"
import { translations } from "../../../utils/Translation/staticTranslation"
const { Option } = Select

const MerchantModal = ({
  selectedRow,
  selectedStatus,
  handleRejectRequest,
  handleApproveRequest,
  handleMoveToPending,
  handleBlockRequest,
  handleUnblockRequest,
  isVisible,
  onCancel,
  onSubmit,
  initialData,
  config = defaultConfig,
  isReadOnly = false,
  loadingRequest = false,
}) => {
  const [form] = Form.useForm()
  const [tenantStatus, setTenantStatus] = useState(initialData?.active_status)
  const [interest, setInterest] = useState("")
  const [currentData, setCurrentData] = useState(null)
  const [blockModalOpen, setBlockModalOpen] = useState(false)
  const [blockReason, setBlockReason] = useState(null)
  // ✅ New state for reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState(null)

  useEffect(() => {
    if (rejectModalOpen) {
      setRejectReason(null)
    }
  }, [rejectModalOpen])

  useEffect(() => {
    if (blockModalOpen) {
      setBlockReason(null)
    }
  }, [blockModalOpen])

  const lang = localStorage.getItem("language") || "ar"
  const request_modal =
    translations[lang]?.MerchantsPage?.merchant_request_modal
  const btn = translations[lang]?.common_btn

  // Helper function to convert country code to country name
  const getCountryName = (code) => {
    if (!code) return lang === "ar" ? "غير معروف" : "Unknown"
    try {
      const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
        type: "region",
      })
      return (
        formatter.of(String(code).toUpperCase()) || String(code).toUpperCase()
      )
    } catch {
      return String(code).toUpperCase()
    }
  }

  function mapDescriptionToInterest(description, config) {
    if (!description) return config.interestOptions[0]?.value || ""

    for (const option of config.interestOptions) {
      if (description.toLowerCase().includes(option.label.toLowerCase())) {
        return option.value
      }
    }

    return config.interestOptions[0]?.value || ""
  }

  const mapInitialDataToFormFields = (data) => {
    if (!data) return {}

    const mappings = {
      vendor_name:
        (lang === "ar" ? data?.vendor_name_arabic : data?.vendor_name) || "",
      email: data.email || "",
      phone: data.phone_no || "",
      businessName:
        (lang === "ar" ? data.name_arabic : data.name_english) ||
        (lang === "ar" ? data.business_name_arabic : data.business_name) ||
        "",
      industry:
        data.industry && Array.isArray(data.industry)
          ? data.industry
              .map((item) => (lang === "ar" ? item.arabic : item.english))
              .join(", ")
          : "",
      platform: data.e_commerce_plateform || "",
      business_url: data.business_url || "",
      description:
        (lang === "ar" ? data.description_arabic : data.description) || "",
      interest: mapDescriptionToInterest(data.description, config),
    }

    return mappings
  }

  useEffect(() => {
    if (isVisible && initialData) {
      form.resetFields()

      setCurrentData(initialData)

      const formValues = mapInitialDataToFormFields(initialData)
      form.setFieldsValue(formValues)

      setTenantStatus(initialData.active_status || false)
      setInterest(formValues.interest)
    }
  }, [isVisible, initialData, form, config])

  const renderFormFields = () => {
    const displayData = currentData
      ? mapInitialDataToFormFields(currentData)
      : {}
    const storeNames = Array.isArray(currentData?.store_names)
      ? currentData.store_names.filter(Boolean)
      : []
    const allStoreNamesText = storeNames.length > 0 ? storeNames.join(", ") : "N/A"

    const isDisabled = selectedStatus === "rejected"

    return (
      <div className="merchant-form-container">
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.vendor_full_name}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {(lang === "ar"
                ? displayData?.vendor_name_arabic
                : displayData?.vendor_name) ||
                (lang === "ar"
                  ? currentData?.vendor_name_arabic
                  : currentData?.vendor_name) ||
                "N/A"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.email_address}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData.email || currentData?.email || "abc@gmail.com"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.phone_number}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData.phone || currentData?.phone_no || "0300-1234567"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.merchant_name}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData.businessName ||
                (lang === "ar"
                  ? currentData?.name_arabic
                  : currentData?.name_english) ||
                (lang === "ar"
                  ? currentData?.business_name_arabic
                  : currentData?.business_name) ||
                "N/A"}
            </div>
          </div>
        </div>
        {selectedStatus === "approved" && (
          <div className="merchant-form-row">
            <div className="merchant-form-label">
              {request_modal?.store_names || "Store Names"}
            </div>
            <div className="merchant-form-value">
              <div
                className={`merchant-text-field ${
                  isDisabled ? "disabled-field" : ""
                }`}
              >
                {allStoreNamesText}
              </div>
            </div>
          </div>
        )}
        <div className="merchant-form-row">
          <div className="merchant-form-label">{request_modal?.industry}</div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData.industry ||
                (Array.isArray(currentData?.industry) &&
                currentData.industry.length > 0
                  ? currentData.industry
                      .map((item) =>
                        lang === "ar" ? item.arabic : item.english,
                      )
                      .join(", ")
                  : "N/A")}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">{request_modal?.country}</div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {currentData?.country
                ? getCountryName(currentData.country)
                : "N/A"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">{request_modal?.city}</div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {currentData?.city || "N/A"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.e_commerce_platform}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData.platform ||
                currentData?.e_commerce_plateform ||
                "N/A"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row">
          <div className="merchant-form-label">
            {request_modal?.business_url}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {currentData?.business_url || "N/A"}
            </div>
          </div>
        </div>
        <div className="merchant-form-row merchant-form-description">
          <div className="merchant-form-label">
            {request_modal?.description}
          </div>
          <div className="merchant-form-value">
            <div
              className={`merchant-text-field merchant-text-area ${
                isDisabled ? "disabled-field" : ""
              }`}
            >
              {displayData?.description ||
                (lang === "ar"
                  ? currentData?.description_arabic
                  : currentData?.description) ||
                "N/A"}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ✅ Rejection reasons array
  const rejectionReasons = [
    {
      en: "Irrelevant product category (not furniture, décor, or home goods).",
      ar: "فئة منتجات غير ذات صلة (ليست أثاثًا أو ديكورًا أو مستلزمات منزلية).",
    },
    {
      en: "Incomplete or incorrect product data",
      ar: "بيانات منتجات غير مكتملة أو غير صحيحة",
    },
    {
      en: "Duplicate products trying to game the system.",
      ar: "تكرار المنتجات بهدف التحايل على النظام.",
    },
    {
      en: "Duplicate accounts/registrations trying to game the system.",
      ar: "تكرار الحسابات أو التسجيلات بهدف التحايل على النظام.",
    },
    {
      en: "Failure to comply with platform standards",
      ar: "عدم الالتزام بمعايير المنصة.",
    },
    {
      en: "Failure to provide valid business license or registration documents.",
      ar: "عدم تقديم رخصة تجارية أو مستندات تسجيل صالحة.",
    },
    {
      en: "Fake brand",
      ar: "علامة تجارية وهمية.",
    },
    {
      en: "Low-quality or counterfeit products that could harm brand credibility.",
      ar: "منتجات منخفضة الجودة أو مقلدة قد تضر بمصداقية العلامة التجارية.",
    },
    {
      en: "Fake personal data",
      ar: "بيانات شخصية مزيفة.",
    },
    {
      en: "Very poor product photos that don't meet design/AI standards.",
      ar: "صور منتجات رديئة للغاية لا تتوافق مع معايير التصميم/الذكاء الاصطناعي.",
    },
    {
      en: "Negative reputation (bad reviews, history of fraud, unresolved complaints, fake discounts).",
      ar: "سمعة سلبية (تقييمات سيئة، تاريخ من الاحتيال، شكاوى غير محلولة، خصومات وهمية).",
    },
  ]

  const renderActionButtons = () => {
    if (isReadOnly) {
      return (
        <button onClick={onCancel} className="merchant-cancel-button">
          {btn?.close}
        </button>
      )
    }
    // Case 1: When status is pending and request_status is not 0
    if (selectedStatus === "pending" && selectedRow?.request_status !== 0) {
      return (
        <>
          {/* ✅ Updated Reject button to open modal */}
          <button
            onClick={() => setRejectModalOpen(true)}
            className="merchant-cancel-button"
          >
            {btn?.reject}
          </button>
          <button
            onClick={() => setBlockModalOpen(true)}
            className="merchant-cancel-button"
          >
            {btn?.block}
          </button>
          <button
            onClick={handleApproveRequest}
            className="merchant-accept-button"
          >
            {btn?.accept}
          </button>
        </>
      )
    }

    // Case 2: When request_status is exactly 0 (custom behavior: only show Reject)
    if (selectedRow?.request_status === 0) {
      return (
        <button
          onClick={() => setRejectModalOpen(true)}
          className="merchant-cancel-button"
        >
          {btn?.reject}
        </button>
      )
    }

    // Case 3: If request was rejected
    if (selectedStatus === "blocked") {
      return (
        <>
          <button onClick={onCancel} className="merchant-cancel-button">
            {btn?.close}
          </button>
          <button
            onClick={handleUnblockRequest}
            className="merchant-accept-button"
          >
            {btn?.unblock}
          </button>
        </>
      )
    }

    // Case 3: If request was blocked

    if (selectedStatus === "rejected") {
      return (
        <>
          <button
            onClick={onCancel}
            className="merchant-cancel-button"
            disabled={loadingRequest}
          >
            {btn?.close}
          </button>
          {!isReadOnly && handleMoveToPending && (
            <button
              onClick={handleMoveToPending}
              className="merchant-accept-button"
              disabled={loadingRequest}
            >
              {loadingRequest
                ? lang === "ar"
                  ? "جاري المعالجة..."
                  : "Processing..."
                : lang === "ar"
                  ? "العودة إلى قيد الانتظار"
                  : "Back to Pending"}
            </button>
          )}
        </>
      )
    }

    // Case 4: Default fallback (approved or any other case)
    return (
      <button onClick={onCancel} className="merchant-cancel-button">
        {btn?.close}
      </button>
    )
  }

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onCancel}
        footer={null}
        className="business-modal merchant-modal"
        style={{ padding: "24px 32px" }}
        width="60%"
        destroyOnClose={true}
      >
        <div className="merchant-modal-content">
          <h2 className="merchant-modal-title">
            {currentData?.name_english ||
              currentData?.business_name ||
              "Tenant/Merchant Name"}
          </h2>
          <Form form={form} layout="vertical" preserve={false}>
            {renderFormFields()}

            <div className="merchant-status-footer">
              {/* Tenant toggle is completely removed */}
              <div className="merchant-actions">{renderActionButtons()}</div>
            </div>
          </Form>
        </div>
      </Modal>

      {/* ✅ Block Modal */}
      <Modal
        open={blockModalOpen}
        onCancel={() => {
          setBlockModalOpen(false)
          setBlockReason(null)
        }}
        footer={null}
        title={btn?.block || "Block Tenant"}
        className="small-block-modal"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label={btn?.select_reason || "Select Reason"} required>
            <Select
              placeholder={btn?.choose_reason || "Choose reason"}
              value={
                blockReason
                  ? lang === "ar"
                    ? blockReason.ar
                    : blockReason.en
                  : undefined
              }
              onChange={(val, option) => {
                // recover full { en, ar } object from option
                setBlockReason(option.reason)
              }}
            >
              {rejectionReasons.map((reason, idx) => (
                <Select.Option
                  key={idx}
                  value={lang === "ar" ? reason.ar : reason.en} // show only one lang in UI
                  reason={reason} // keep both langs attached
                >
                  {lang === "ar" ? reason.ar : reason.en}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <button
              onClick={() => {
                setBlockModalOpen(false)
                setBlockReason(null)
              }}
              className="merchant-cancel-button"
            >
              {btn?.cancel || "Cancel"}
            </button>
            <button
              onClick={() => {
                if (blockReason) {
                  handleBlockRequest({
                    en: blockReason.en,
                    ar: blockReason.ar,
                  })
                  setBlockReason(null)
                  setBlockModalOpen(false)
                }
              }}
              disabled={!blockReason}
              className="merchant-accept-button"
            >
              {btn?.confirm || "Confirm Block"}
            </button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false)
          setRejectReason(null)
        }}
        footer={null}
        title={btn?.reject || "Reject Tenant"}
        className="small-block-modal"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label={btn?.select_reason || "Select Reason"} required>
            <Select
              placeholder={btn?.choose_reason || "Choose reason"}
              value={
                rejectReason
                  ? lang === "ar"
                    ? rejectReason.ar
                    : rejectReason.en
                  : undefined
              }
              onChange={(val, option) => {
                // recover full { en, ar } object from option
                setRejectReason(option.reason)
              }}
            >
              {rejectionReasons.map((reason, idx) => (
                <Select.Option
                  key={idx}
                  value={lang === "ar" ? reason.ar : reason.en} // show only one lang in UI
                  reason={reason} // keep both langs attached
                >
                  {lang === "ar" ? reason.ar : reason.en}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <button
              onClick={() => {
                setRejectModalOpen(false)
                setRejectReason(null)
              }}
              className="merchant-cancel-button"
            >
              {btn?.cancel || "Cancel"}
            </button>
            <button
              onClick={() => {
                if (rejectReason) {
                  handleRejectRequest({
                    en: rejectReason.en,
                    ar: rejectReason.ar,
                  })
                  setRejectReason(null)
                  setRejectModalOpen(false)
                }
              }}
              disabled={!rejectReason}
              className="merchant-accept-button"
            >
              {btn?.confirm || "Confirm Reject"}
            </button>
          </div>
        </Form>
      </Modal>
    </>
  )
}

export default MerchantModal
